import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { DwcDiagnosticPanel } from './dwc-diagnostic-panel';
import { useQueryClient } from '@tanstack/react-query';
import {
  useGetDwcConnections, useSaveDwcConnection, useGetDwcLocations, useTestDwcConnection,
  usePreviewDwcConnection, useStartDwcSync, useGetDwcRuns,
  getGetDwcConnectionsQueryKey, getGetDwcLocationsQueryKey, getGetDwcRunsQueryKey,
} from '@workspace/api-client-react';
import type { DwcConnection, DwcConnectionInput, DwcRun, DwcTestResult } from '@workspace/api-client-react';
import {
  Loader2, CheckCircle2, XCircle, AlertTriangle, KeyRound, Save, MapPin, Warehouse,
  ShieldAlert, Eye, Play, RefreshCcw, Plus, Clock, ChevronDown, ChevronUp,
} from 'lucide-react';

interface Store { id: string; nickname: string; storeUrl: string }

function errMsg(e: unknown): string {
  const anyE = e as { data?: { error?: string; message?: string }; message?: string } | null;
  return anyE?.data?.error ?? anyE?.data?.message ?? anyE?.message ?? 'Request failed';
}

function auDate(iso: string | null | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '-';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

const READY_TTL_MS = 15 * 60 * 1000;
const ACTIVE: DwcRun['status'][] = ['previewing', 'running'];

interface Draft {
  storeId: string;
  environment: 'dev' | 'prod';
  clientId: string;
  clientSecret: string;
  subscriptionKey: string;
  warehouses: string;
  targetLocationId: string;
  cap: string;
  networkApproved: boolean;
}

function draftFrom(c: DwcConnection | null, stores: Store[]): Draft {
  return {
    storeId: c?.storeId ?? stores[0]?.id ?? '',
    environment: c?.environment ?? 'dev',
    clientId: '', clientSecret: '', subscriptionKey: '',
    warehouses: c?.warehouseCodes.join(', ') ?? '',
    targetLocationId: c?.targetLocationId ?? '',
    cap: c?.maxInventoryCap != null ? String(c.maxInventoryCap) : '',
    networkApproved: c?.networkApproved ?? false,
  };
}

function parseCodes(s: string): string[] {
  return Array.from(new Set(s.split(',').map((x) => x.trim()).filter(Boolean)));
}

function isDirty(d: Draft, c: DwcConnection | null, stores: Store[]): boolean {
  const base = draftFrom(c, stores);
  return d.storeId !== base.storeId || d.environment !== base.environment
    || !!d.clientId || !!d.clientSecret || !!d.subscriptionKey
    || parseCodes(d.warehouses).join(',') !== parseCodes(base.warehouses).join(',')
    || d.targetLocationId !== base.targetLocationId || d.cap.trim() !== base.cap
    || d.networkApproved !== base.networkApproved;
}

const inputCls = 'w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-violet-500/40';
const labelCls = 'block text-xs font-medium text-muted-foreground mb-1';
const btn = 'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed';

// ── Main section ────────────────────────────────────────────────────────────

export function DynamicWheelStock({ token, stores }: { token: string | null; stores: Store[] }) {
  // Parent remounts on auth changes. Random scope prevents cross-account cache reuse.
  const [scope] = useState(() => crypto.randomUUID());
  const queryClient = useQueryClient();
  const request = useMemo<Req>(() => {
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    return { headers };
  }, [token]);
  const [creating, setCreating] = useState(false);

  const connsQ = useGetDwcConnections({
    request,
    query: { enabled: !!token, queryKey: [...getGetDwcConnectionsQueryKey(), scope], retry: false, refetchInterval: 30000 },
  });

  // Reset local UI state on user change
  useEffect(() => { setCreating(false); }, [scope]);
  useEffect(() => () => {
    queryClient.removeQueries({ predicate: query => query.queryKey.includes(scope) });
  }, [queryClient, scope]);

  const conns = connsQ.data ?? [];

  return (
    <section className="rounded-xl border border-border bg-muted/20 overflow-hidden" data-testid="section-dwc">
      <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-border">
        <div>
          <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Warehouse className="w-4 h-4 text-violet-400" /> Dynamic Wheel Co
            <span className="rounded bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-medium text-violet-300">Stock only</span>
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Previews supplier quantities against your Shopify SKUs. Nothing is written until you approve a preview.
          </p>
        </div>
        {!creating && (
          <button className={`${btn} bg-violet-500/15 text-violet-300 hover:bg-violet-500/25`} onClick={() => setCreating(true)} disabled={!token || stores.length === 0} data-testid="button-dwc-new">
            <Plus className="w-3.5 h-3.5" /> New connection
          </button>
        )}
      </div>

      <div className="p-4 space-y-3">
        {!!token && <DwcDiagnosticPanel request={request} scope={scope} formatDate={auDate} />}
        {connsQ.isLoading && (
          <div className="space-y-2">
            <div className="h-14 rounded-lg bg-muted/40 animate-pulse" />
            <div className="h-14 rounded-lg bg-muted/30 animate-pulse" />
          </div>
        )}
        {connsQ.isError && (
          <div className="flex items-center justify-between gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
            <span className="flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5" /> Could not load Dynamic Wheel Co connections: {errMsg(connsQ.error)}</span>
            <button className={`${btn} text-red-200 hover:bg-red-500/20`} onClick={() => connsQ.refetch()} data-testid="button-dwc-retry">Retry</button>
          </div>
        )}
        {stores.length === 0 && (
          <p className="text-xs text-muted-foreground">Connect a Shopify store first to choose a destination for supplier stock.</p>
        )}
        {creating && (
          <ConnectionCard key={`new-${scope}`} conn={null} stores={stores} request={request} scope={scope} onDone={() => setCreating(false)} />
        )}
        {connsQ.isSuccess && conns.length === 0 && !creating && (
          <div className="rounded-lg border border-dashed border-border px-4 py-6 text-center">
            <p className="text-sm text-foreground">No Dynamic Wheel Co connection yet.</p>
            <p className="text-xs text-muted-foreground mt-1">You will need the client ID, client secret and subscription key issued by the supplier.</p>
          </div>
        )}
        {conns.map((c) => (
          <ConnectionCard key={`${scope}-${c.id}`} conn={c} stores={stores} request={request} scope={scope} />
        ))}
      </div>
    </section>
  );
}

// ── Connection editor + actions ──────────────────────────────────────────────

type Req = { headers: Record<string, string> };

function ConnectionCard({ conn, stores, request, scope, onDone }: {
  conn: DwcConnection | null; stores: Store[]; request: Req; scope: string; onDone?: () => void;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(conn === null);
  const [draft, setDraft] = useState<Draft>(() => draftFrom(conn, stores));
  const [formErr, setFormErr] = useState<string | null>(null);
  const [savedNote, setSavedNote] = useState<string | null>(null);
  const [test, setTest] = useState<DwcTestResult | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [wantLocations, setWantLocations] = useState(false);

  const id = conn?.id ?? '';
  const dirty = isDirty(draft, conn, stores);
  const store = stores.find((s) => s.id === (conn?.storeId ?? draft.storeId));
  const connsKey = [...getGetDwcConnectionsQueryKey(), scope];
  const runsKey = [...getGetDwcRunsQueryKey(id), scope];

  const save = useSaveDwcConnection({ request });
  const testM = useTestDwcConnection({ request });
  const previewM = usePreviewDwcConnection({ request });
  const syncM = useStartDwcSync({ request });

  const locQ = useGetDwcLocations(id, {
    request,
    query: { enabled: !!id && wantLocations, queryKey: [...getGetDwcLocationsQueryKey(id), scope], retry: false, staleTime: 60_000 },
  });

  const runsQ = useGetDwcRuns(id, {
    request,
    query: {
      enabled: !!id,
      queryKey: runsKey,
      retry: false,
      refetchInterval: (q) => {
        const d = q.state.data as DwcRun[] | undefined;
        return d?.some((r) => ACTIVE.includes(r.status)) ? 3000 : 30000;
      },
    },
  });
  const runs = runsQ.data ?? [];
  const active = runs.find((r) => ACTIVE.includes(r.status));
  const latestReady = runs.find((r) => r.status === 'ready');
  const readyFresh = latestReady && Date.now() - new Date(latestReady.completedAt ?? latestReady.startedAt).getTime() < READY_TTL_MS
    && conn && new Date(conn.updatedAt).getTime() <= new Date(latestReady.startedAt).getTime() ? latestReady : null;

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => { setDraft((d) => ({ ...d, [k]: v })); setSavedNote(null); };

  function submit() {
    setFormErr(null);
    if (!draft.storeId) return setFormErr('Choose a destination store.');
    const codes = parseCodes(draft.warehouses);
    if (codes.length > 100) return setFormErr('At most 100 warehouse codes.');
    const loc = draft.targetLocationId.trim();
    if (loc && !/^[0-9]+$/.test(loc)) return setFormErr('Target location ID must be numeric.');
    const capRaw = draft.cap.trim();
    const cap = capRaw === '' ? null : Number(capRaw);
    if (cap !== null && (!Number.isInteger(cap) || cap < 0 || cap > 2147483647)) return setFormErr('Inventory cap must be a whole number of 0 or more.');
    const secrets = { clientId: draft.clientId.trim(), clientSecret: draft.clientSecret.trim(), subscriptionKey: draft.subscriptionKey.trim() };
    if (!conn && (!secrets.clientId || !secrets.clientSecret || !secrets.subscriptionKey)) return setFormErr('A new connection needs client ID, client secret and subscription key.');

    const data: DwcConnectionInput = {
      ...(conn ? { id: conn.id } : {}),
      storeId: draft.storeId,
      environment: draft.environment,
      warehouseCodes: codes,
      targetLocationId: loc || null,
      maxInventoryCap: cap,
      networkApproved: draft.networkApproved,
      ...(secrets.clientId ? { clientId: secrets.clientId } : {}),
      ...(secrets.clientSecret ? { clientSecret: secrets.clientSecret } : {}),
      ...(secrets.subscriptionKey ? { subscriptionKey: secrets.subscriptionKey } : {}),
    };
    save.mutate({ data }, {
      onSuccess: (saved) => {
        setDraft(draftFrom(saved, stores));
        setTest(null);
        qc.setQueryData<DwcConnection[]>(connsKey, (old) => {
          const list = old ?? [];
          return list.some((c) => c.id === saved.id) ? list.map((c) => (c.id === saved.id ? saved : c)) : [...list, saved];
        });
        qc.invalidateQueries({ queryKey: connsKey });
        if (conn) { setSavedNote('Saved. Any earlier preview is no longer valid.'); qc.invalidateQueries({ queryKey: runsKey }); }
        onDone?.();
      },
      onError: (e) => setFormErr(errMsg(e)),
    });
  }

  const gated = !conn || dirty || !conn.networkApproved || !conn.credentialsConfigured;
  const gateReason = !conn ? 'Save the connection first.'
    : dirty ? 'Save or discard your edits first.'
    : !conn.credentialsConfigured ? 'Credentials are not configured.'
    : !conn.networkApproved ? 'Confirm supplier network approval and save first.' : null;

  function runTest() {
    if (!conn) return;
    setActionErr(null); setTest(null);
    testM.mutate({ id: conn.id }, { onSuccess: setTest, onError: (e) => setActionErr(errMsg(e)) });
  }
  function runPreview() {
    if (!conn) return;
    setActionErr(null);
    previewM.mutate({ id: conn.id }, {
      onSuccess: (run) => qc.setQueryData<DwcRun[]>(runsKey, (old) => [run, ...(old ?? []).filter((r) => r.id !== run.id)]),
      onError: (e) => { setActionErr(errMsg(e)); runsQ.refetch(); },
      onSettled: () => qc.invalidateQueries({ queryKey: runsKey }),
    });
  }
  function approve(run: DwcRun) {
    if (!conn || dirty) return;
    setActionErr(null);
    syncM.mutate({ id: conn.id, data: { previewId: run.id } }, {
      onSuccess: (r) => qc.setQueryData<DwcRun[]>(runsKey, (old) => (old ?? []).map((x) => (x.id === r.id ? r : x)).concat((old ?? []).some((x) => x.id === r.id) ? [] : [r])),
      onError: (e) => setActionErr(errMsg(e)),
      onSettled: () => qc.invalidateQueries({ queryKey: runsKey }),
    });
  }

  const savedCodes = new Set(parseCodes(draft.warehouses));
  const toggleCode = (code: string) => {
    const s = new Set(savedCodes);
    s.has(code) ? s.delete(code) : s.add(code);
    set('warehouses', Array.from(s).join(', '));
  };

  return (
    <div className="rounded-lg border border-border bg-background/40 overflow-hidden" data-testid={`card-dwc-${id || 'new'}`}>
      {/* Header */}
      <div className="flex items-center gap-3 px-3 py-2.5">
        <div className={`w-2 h-2 rounded-full shrink-0 ${conn?.networkApproved && conn.credentialsConfigured ? 'bg-emerald-400' : 'bg-amber-400'}`} />
        <button className="flex-1 min-w-0 text-left" onClick={() => setOpen(!open)} data-testid={`button-dwc-toggle-${id || 'new'}`}>
          <div className="text-sm font-medium text-foreground truncate">
            {conn ? `Dynamic Wheel Co to ${store?.nickname ?? 'unknown store'}` : 'New Dynamic Wheel Co connection'}
          </div>
          <div className="text-xs text-muted-foreground truncate">
            {conn ? `${conn.environment.toUpperCase()} · client ${conn.clientIdMasked || 'not set'} · ${conn.warehouseCodes.length} warehouse code${conn.warehouseCodes.length === 1 ? '' : 's'} · updated ${auDate(conn.updatedAt)}` : 'Not saved yet'}
          </div>
        </button>
        {conn && <RunChip run={runs[0]} />}
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </div>

      {open && (
        <div className="border-t border-border p-3 space-y-4">
          {/* Settings */}
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Destination store</label>
              <select className={inputCls} value={draft.storeId} onChange={(e) => { set('storeId', e.target.value); set('targetLocationId', ''); }} data-testid="select-dwc-store">
                {stores.map((s) => <option key={s.id} value={s.id}>{s.nickname} ({s.storeUrl})</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Supplier environment</label>
              <select className={inputCls} value={draft.environment} onChange={(e) => set('environment', e.target.value as 'dev' | 'prod')} data-testid="select-dwc-env">
                <option value="dev">Development (dev)</option>
                <option value="prod">Production (prod)</option>
              </select>
            </div>
          </div>

          <div className="rounded-lg border border-border p-3 space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1.5 font-medium text-foreground"><KeyRound className="w-3.5 h-3.5 text-violet-400" /> Credentials</span>
              {conn && (conn.credentialsConfigured
                ? <span className="text-emerald-400">Saved (client {conn.clientIdMasked}). Leave blank to keep.</span>
                : <span className="text-amber-300">Not configured</span>)}
            </div>
            <div className="grid gap-2 sm:grid-cols-3">
              <input className={inputCls} type="password" autoComplete="off" placeholder="Client ID" value={draft.clientId} onChange={(e) => set('clientId', e.target.value)} data-testid="input-dwc-client-id" />
              <input className={inputCls} type="password" autoComplete="off" placeholder="Client secret" value={draft.clientSecret} onChange={(e) => set('clientSecret', e.target.value)} data-testid="input-dwc-client-secret" />
              <input className={inputCls} type="password" autoComplete="off" placeholder="Subscription key" value={draft.subscriptionKey} onChange={(e) => set('subscriptionKey', e.target.value)} data-testid="input-dwc-subscription-key" />
            </div>
          </div>

          <div>
            <label className={labelCls}>Warehouse codes (comma separated, as given by the supplier)</label>
            <input className={`${inputCls} font-mono`} placeholder="e.g. CODE1, CODE2" value={draft.warehouses} onChange={(e) => set('warehouses', e.target.value)} data-testid="input-dwc-warehouses" />
            <p className="text-[11px] text-muted-foreground mt-1">Stock from these codes is summed per SKU. Confirm what each code means with the supplier.</p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Target Shopify location</label>
              {locQ.data && locQ.data.length > 0 ? (
                <select className={inputCls} value={draft.targetLocationId} onChange={(e) => set('targetLocationId', e.target.value)} data-testid="select-dwc-location">
                  <option value="">Not set</option>
                  {draft.targetLocationId && !locQ.data.some((l) => l.id === draft.targetLocationId) && <option value={draft.targetLocationId}>ID {draft.targetLocationId}</option>}
                  {locQ.data.map((l) => <option key={l.id} value={l.id}>{l.name} ({l.id})</option>)}
                </select>
              ) : (
                <input className={`${inputCls} font-mono`} inputMode="numeric" placeholder="Numeric location ID" value={draft.targetLocationId} onChange={(e) => set('targetLocationId', e.target.value)} data-testid="input-dwc-location" />
              )}
              {conn && (
                <button className={`${btn} mt-1 px-0 text-violet-300 hover:text-violet-200`} onClick={() => (wantLocations ? locQ.refetch() : setWantLocations(true))} disabled={locQ.isFetching || draft.storeId !== conn.storeId} data-testid="button-dwc-load-locations">
                  {locQ.isFetching ? <Loader2 className="w-3 h-3 animate-spin" /> : <MapPin className="w-3 h-3" />} Load active locations from saved store
                </button>
              )}
              {locQ.isError && <p className="text-[11px] text-red-300">{errMsg(locQ.error)}</p>}
            </div>
            <div>
              <label className={labelCls}>Max inventory cap (optional)</label>
              <input className={inputCls} inputMode="numeric" placeholder="No cap" value={draft.cap} onChange={(e) => set('cap', e.target.value)} data-testid="input-dwc-cap" />
            </div>
          </div>

          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 space-y-2">
            <div className="flex items-start gap-2 text-xs text-amber-200">
              <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
              <span>The supplier may restrict API access by IP address. SoraFlo does not provide a static IP and does not solve IP allowlisting. Only tick this if Dynamic Wheel Co has explicitly approved the outbound access arrangement for this account.</span>
            </div>
            <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer">
              <input type="checkbox" checked={draft.networkApproved} onChange={(e) => set('networkApproved', e.target.checked)} data-testid="checkbox-dwc-network-approved" />
              Supplier has approved network access
            </label>
          </div>

          {formErr && <Notice tone="error">{formErr}</Notice>}
          {savedNote && <Notice tone="ok">{savedNote}</Notice>}

          <div className="flex flex-wrap items-center gap-2">
            <button className={`${btn} bg-violet-600 text-white hover:bg-violet-500`} onClick={submit} disabled={save.isPending || (!!conn && !dirty)} data-testid="button-dwc-save">
              {save.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
            </button>
            {(dirty || !conn) && (
              <button className={`${btn} text-muted-foreground hover:bg-muted/50`} onClick={() => { setDraft(draftFrom(conn, stores)); setFormErr(null); if (!conn) onDone?.(); }} data-testid="button-dwc-discard">
                {conn ? 'Discard edits' : 'Cancel'}
              </button>
            )}
            {conn && dirty && <span className="text-[11px] text-amber-300">Unsaved edits. Saving will invalidate earlier previews.</span>}
          </div>

          {/* Actions */}
          {conn && (
            <div className="border-t border-border pt-3 space-y-3">
              {gateReason && <p className="text-[11px] text-muted-foreground">Test and preview unavailable: {gateReason}</p>}
              <div className="flex flex-wrap gap-2">
                <button className={`${btn} border border-border text-foreground hover:bg-muted/50`} onClick={runTest} disabled={gated || testM.isPending} data-testid="button-dwc-test">
                  {testM.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Test connection (read only)
                </button>
                <button className={`${btn} border border-violet-500/40 text-violet-200 hover:bg-violet-500/15`} onClick={runPreview} disabled={gated || previewM.isPending || !!active} data-testid="button-dwc-preview">
                  {previewM.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />} Build preview
                </button>
                <button className={`${btn} text-muted-foreground hover:bg-muted/50`} onClick={() => runsQ.refetch()} disabled={runsQ.isFetching} data-testid="button-dwc-refresh">
                  <RefreshCcw className={`w-3.5 h-3.5 ${runsQ.isFetching ? 'animate-spin' : ''}`} /> Refresh
                </button>
              </div>
              {actionErr && <Notice tone="error">{actionErr}</Notice>}
              {test && (
                <div className={`rounded-lg border px-3 py-2 text-xs space-y-2 ${test.success ? 'border-emerald-500/30 bg-emerald-500/10' : 'border-red-500/30 bg-red-500/10'}`} data-testid="status-dwc-test">
                  <div className={`flex items-center gap-1.5 ${test.success ? 'text-emerald-300' : 'text-red-300'}`}>
                    {test.success ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />} {test.message}
                  </div>
                  {test.warehouseCodes.length > 0 && (
                    <div>
                      <p className="text-muted-foreground mb-1">Codes seen in a sample response (may be incomplete). Selecting changes your editor; save to apply.</p>
                      <div className="flex flex-wrap gap-1.5">
                        {test.warehouseCodes.map((code) => (
                          <button key={code} onClick={() => toggleCode(code)} className={`rounded px-2 py-0.5 font-mono text-[11px] border ${savedCodes.has(code) ? 'border-violet-500/60 bg-violet-500/20 text-violet-200' : 'border-border text-muted-foreground hover:bg-muted/50'}`} data-testid={`button-dwc-code-${code}`}>
                            {savedCodes.has(code) ? 'Selected: ' : ''}{code}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <RunHistory
                runs={runs} loading={runsQ.isLoading} error={runsQ.isError ? errMsg(runsQ.error) : null}
                readyId={readyFresh?.id ?? null} dirty={dirty} storeLabel={store ? `${store.nickname} (${store.storeUrl})` : 'unknown store'}
                locationId={conn.targetLocationId} cap={conn.maxInventoryCap}
                approving={syncM.isPending} onApprove={approve} onRetry={() => runsQ.refetch()}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Notice({ tone, children }: { tone: 'error' | 'ok'; children: ReactNode }) {
  return (
    <div className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-xs ${tone === 'error' ? 'border-red-500/30 bg-red-500/10 text-red-300' : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'}`}>
      {tone === 'error' ? <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> : <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5" />}
      <span>{children}</span>
    </div>
  );
}

const STATUS_STYLE: Record<DwcRun['status'], string> = {
  previewing: 'text-violet-300', ready: 'text-sky-300', running: 'text-violet-300', done: 'text-emerald-400', error: 'text-red-400',
};
const STATUS_LABEL: Record<DwcRun['status'], string> = {
  previewing: 'Building preview', ready: 'Preview ready', running: 'Writing stock', done: 'Done', error: 'Error',
};

function RunChip({ run }: { run?: DwcRun }) {
  if (!run) return null;
  const spin = ACTIVE.includes(run.status);
  return (
    <span className={`hidden sm:flex items-center gap-1 text-xs ${STATUS_STYLE[run.status]}`}>
      {spin ? <Loader2 className="w-3 h-3 animate-spin" /> : run.status === 'error' ? <XCircle className="w-3 h-3" /> : run.status === 'ready' ? <Clock className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
      {STATUS_LABEL[run.status]}
    </span>
  );
}

// ── History ─────────────────────────────────────────────────────────────────

function RunHistory({ runs, loading, error, readyId, dirty, storeLabel, locationId, cap, approving, onApprove, onRetry }: {
  runs: DwcRun[]; loading: boolean; error: string | null; readyId: string | null; dirty: boolean;
  storeLabel: string; locationId: string | null; cap: number | null; approving: boolean;
  onApprove: (r: DwcRun) => void; onRetry: () => void;
}) {
  if (loading) return <div className="h-20 rounded-lg bg-muted/30 animate-pulse" />;
  if (error) return (
    <div className="flex items-center justify-between rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
      <span>Could not load run history: {error}</span>
      <button className={`${btn} hover:bg-red-500/20`} onClick={onRetry} data-testid="button-dwc-runs-retry">Retry</button>
    </div>
  );
  if (runs.length === 0) return <p className="text-xs text-muted-foreground">No previews or syncs yet. Build a preview to see what would change.</p>;
  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-foreground">History</h3>
      {runs.map((r, i) => (
        <RunRow key={r.id} run={r} defaultOpen={i === 0} canApprove={r.id === readyId && !dirty} expiredReady={r.status === 'ready' && r.id !== readyId}
          dirty={dirty} storeLabel={storeLabel} locationId={locationId} cap={cap} approving={approving} onApprove={onApprove} />
      ))}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="rounded-md bg-muted/30 px-2 py-1.5">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-sm font-semibold tabular-nums ${tone ?? 'text-foreground'}`}>{value.toLocaleString('en-AU')}</div>
    </div>
  );
}

function RunRow({ run, defaultOpen, canApprove, expiredReady, dirty, storeLabel, locationId, cap, approving, onApprove }: {
  run: DwcRun; defaultOpen: boolean; canApprove: boolean; expiredReady: boolean; dirty: boolean;
  storeLabel: string; locationId: string | null; cap: number | null; approving: boolean; onApprove: (r: DwcRun) => void;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [confirming, setConfirming] = useState(false);
  const unattempted = Math.max(0, run.totalToUpdate - run.updatedCount - run.failedCount);
  const isWrite = run.status === 'running' || run.status === 'done' || (run.status === 'error' && run.updatedCount + run.failedCount > 0);

  return (
    <div className="rounded-lg border border-border" data-testid={`row-dwc-run-${run.id}`}>
      <button className="flex w-full items-center gap-2 px-3 py-2 text-left" onClick={() => setOpen(!open)}>
        <span className={`text-xs font-medium ${STATUS_STYLE[run.status]}`}>{STATUS_LABEL[run.status]}{expiredReady ? ' (expired or superseded)' : ''}</span>
        <span className="text-[11px] text-muted-foreground">{auDate(run.startedAt)}{run.completedAt ? ` to ${auDate(run.completedAt)}` : ''}</span>
        <span className="ml-auto">{open ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />}</span>
      </button>
      {open && (
        <div className="border-t border-border px-3 py-2 space-y-2 text-xs">
          {ACTIVE.includes(run.status) && (
            <p className="flex items-center gap-1.5 text-violet-300"><Loader2 className="w-3 h-3 animate-spin" /> Phase: {run.phase || 'starting'} · {run.pagesDone} page{run.pagesDone === 1 ? '' : 's'} read</p>
          )}
          {run.message && <p className={run.status === 'error' ? 'text-red-300' : 'text-muted-foreground'}>{run.message}</p>}
          <p className="text-muted-foreground">Warehouses: <span className="font-mono">{run.warehouseCodes.join(', ') || '-'}</span></p>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5">
            <Stat label="Supplier SKUs" value={run.supplierSkuCount} />
            <Stat label="Matched" value={run.matchedCount} tone="text-emerald-400" />
            <Stat label="Unmatched" value={run.unmatchedCount} tone="text-amber-300" />
            <Stat label="Ambiguous" value={run.ambiguousCount} tone="text-amber-300" />
            <Stat label="Skipped" value={run.skippedCount} />
            <Stat label="To change" value={run.totalToUpdate} tone="text-violet-300" />
          </div>
          {isWrite && (
            <div className="grid grid-cols-3 gap-1.5">
              <Stat label="Written" value={run.updatedCount} tone="text-emerald-400" />
              <Stat label="Failed" value={run.failedCount} tone="text-red-400" />
              <Stat label="Not attempted" value={unattempted} />
            </div>
          )}
          {run.sample.length > 0 && (
            <div>
              <p className="text-muted-foreground mb-1">Sample of changes (first {run.sample.length} of {run.totalToUpdate.toLocaleString('en-AU')}):</p>
              <div className="max-h-48 overflow-auto rounded-md border border-border">
                <table className="w-full text-[11px]">
                  <thead className="sticky top-0 bg-muted/80 text-muted-foreground"><tr><th className="px-2 py-1 text-left">SKU</th><th className="px-2 py-1 text-right">Current</th><th className="px-2 py-1 text-right">New</th></tr></thead>
                  <tbody>
                    {run.sample.map((s) => (
                      <tr key={s.sku} className="border-t border-border/60"><td className="px-2 py-1 font-mono">{s.sku}</td><td className="px-2 py-1 text-right tabular-nums">{s.currentQuantity}</td><td className="px-2 py-1 text-right tabular-nums text-violet-200">{s.quantity}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {run.ambiguousSample.length > 0 && <p className="text-amber-300/90">Ambiguous SKUs (sample, not written): <span className="font-mono">{run.ambiguousSample.join(', ')}</span></p>}
          {run.unmatchedSample.length > 0 && <p className="text-muted-foreground">Unmatched SKUs (sample): <span className="font-mono">{run.unmatchedSample.join(', ')}</span></p>}

          {run.status === 'ready' && (
            canApprove ? (
              confirming ? (
                <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 space-y-2">
                  <p className="text-red-200">
                    This will overwrite available quantities for {run.totalToUpdate.toLocaleString('en-AU')} SKUs in <strong>{storeLabel}</strong> at location <span className="font-mono">{locationId ?? 'default from saved settings'}</span>{cap != null ? `, capped at ${cap}` : ''}, using the quantities in this preview. This cannot be undone automatically.
                  </p>
                  <div className="flex gap-2">
                    <button className={`${btn} bg-red-600 text-white hover:bg-red-500`} disabled={approving || dirty} onClick={() => { onApprove(run); setConfirming(false); }} data-testid="button-dwc-confirm-sync">
                      {approving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Write stock now
                    </button>
                    <button className={`${btn} text-muted-foreground hover:bg-muted/50`} onClick={() => setConfirming(false)} data-testid="button-dwc-cancel-sync">Cancel</button>
                  </div>
                </div>
              ) : (
                <button className={`${btn} bg-violet-600 text-white hover:bg-violet-500`} onClick={() => setConfirming(true)} disabled={run.totalToUpdate === 0} data-testid="button-dwc-approve">
                  <Play className="w-3.5 h-3.5" /> Approve and write stock
                </button>
              )
            ) : (
              <p className="text-[11px] text-muted-foreground">{dirty ? 'Unsaved edits: approval disabled.' : 'Preview expired (15 minutes) or settings changed. Build a new preview.'}</p>
            )
          )}
        </div>
      )}
    </div>
  );
}
